import { useState, useEffect, useCallback } from 'react'
import {
  openShift,
  getShifts,
  getActiveShift,
  getShiftDetail,
  closeShift,
  addCashEntry,
  getCashEntries,
  validateShiftOpening,
  validateShiftClosing,
  validateCashEntry,
  calculateExpectedCash,
  calculateCashDifference
} from '../api/shiftService.js'

/**
 * Hook for managing shifts list with pagination
 */
export const useShifts = (initialParams = {}) => {
  const [shifts, setShifts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  })

  const fetchShifts = useCallback(async (params = {}) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await getShifts({
        page: pagination.page,
        limit: pagination.limit,
        ...params
      })
      
      setShifts(response.data || [])
      setPagination(prev => ({
        ...prev,
        total: response.meta?.total || 0
      }))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [pagination.page, pagination.limit])

  const changePage = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }, [])

  const changeLimit = useCallback((newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }))
  }, [])

  const refresh = useCallback(() => {
    fetchShifts()
  }, [fetchShifts])

  useEffect(() => {
    fetchShifts(initialParams)
  }, [fetchShifts])

  return {
    shifts,
    loading,
    error,
    pagination,
    changePage,
    changeLimit,
    refresh
  }
}

/**
 * Hook for managing active shift status
 */
export const useActiveShift = () => {
  const [activeShift, setActiveShift] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasActiveShift, setHasActiveShift] = useState(false)

  const checkActiveShift = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await getActiveShift()
      setActiveShift(response.data)
      setHasActiveShift(true)
      // Cache the cashier name for receipt fallback (when transaction
      // payload doesn't include cashier_name).
      const cashierName =
        response.data?.opened_by_name || response.data?.opened_by || ''
      if (cashierName) {
        try { localStorage.setItem('activeShiftCashierName', cashierName) } catch {}
      }
    } catch (err) {
      if (err.message.includes('SHIFT_NOT_OPEN') || err.message.includes('no active shift')) {
        setActiveShift(null)
        setHasActiveShift(false)
        setError(null) // This is expected when no shift is open
        try { localStorage.removeItem('activeShiftCashierName') } catch {}
      } else {
        setError(err.message)
        setActiveShift(null)
        setHasActiveShift(false)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshActiveShift = useCallback(() => {
    checkActiveShift()
  }, [checkActiveShift])

  useEffect(() => {
    checkActiveShift()
  }, [checkActiveShift])

  return {
    activeShift,
    hasActiveShift,
    loading,
    error,
    refreshActiveShift
  }
}

/**
 * Hook for managing shift operations (open, close)
 */
export const useShiftOperations = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleOpenShift = useCallback(async (shiftData) => {
    try {
      setLoading(true)
      setError(null)

      // Validate data first
      const validation = validateShiftOpening(shiftData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await openShift(shiftData)
      return response.data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const handleCloseShift = useCallback(async (shiftId, closingData) => {
    try {
      setLoading(true)
      setError(null)

      // Validate data first
      const validation = validateShiftClosing(closingData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await closeShift(shiftId, closingData)
      return response.data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    loading,
    error,
    openShift: handleOpenShift,
    closeShift: handleCloseShift
  }
}

/**
 * Hook for managing a single shift detail
 */
export const useShiftDetail = (shiftId) => {
  const [shift, setShift] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchShiftDetail = useCallback(async () => {
    if (!shiftId) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await getShiftDetail(shiftId)
      setShift(response.data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [shiftId])

  const refresh = useCallback(() => {
    fetchShiftDetail()
  }, [fetchShiftDetail])

  useEffect(() => {
    fetchShiftDetail()
  }, [fetchShiftDetail])

  return {
    shift,
    loading,
    error,
    refresh
  }
}

/**
 * Hook for managing cash entries for a shift
 */
export const useCashEntries = (shiftId) => {
  const [cashEntries, setCashEntries] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchCashEntries = useCallback(async () => {
    if (!shiftId) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await getCashEntries(shiftId)
      setCashEntries(response.data || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [shiftId])

  const addEntry = useCallback(async (entryData) => {
    try {
      setLoading(true)
      setError(null)

      // Validate data first
      const validation = validateCashEntry(entryData)
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '))
      }

      const response = await addCashEntry(shiftId, entryData)
      
      // Refresh cash entries list
      await fetchCashEntries()
      
      return response.data
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setLoading(false)
    }
  }, [shiftId, fetchCashEntries])

  const refresh = useCallback(() => {
    fetchCashEntries()
  }, [fetchCashEntries])

  useEffect(() => {
    fetchCashEntries()
  }, [fetchCashEntries])

  return {
    cashEntries,
    loading,
    error,
    addEntry,
    refresh
  }
}

/**
 * Hook for shift calculations and summary
 */
export const useShiftCalculations = (shift) => {
  const [calculations, setCalculations] = useState({
    expectedCash: 0,
    cashDifference: 0,
    grandTotal: 0,
    isBalanced: true
  })

  useEffect(() => {
    if (shift) {
      const expectedCash = calculateExpectedCash(shift)
      const cashDifference = shift.closing_cash 
        ? calculateCashDifference(shift.closing_cash, expectedCash)
        : 0
      const grandTotal = (shift.total_cash_sales || 0) + (shift.total_non_cash_sales || 0)
      const isBalanced = Math.abs(cashDifference) < 1000 // Allow 1000 IDR tolerance

      setCalculations({
        expectedCash,
        cashDifference,
        grandTotal,
        isBalanced
      })
    }
  }, [shift])

  return calculations
}

/**
 * Hook for shift status checking before transactions
 */
export const useShiftGuard = () => {
  const { activeShift, hasActiveShift, loading, refreshActiveShift } = useActiveShift()
  
  const canProcessTransaction = !loading && hasActiveShift && activeShift?.status === 'OPEN'
  const needsShiftOpen = !loading && !hasActiveShift
  
  return {
    canProcessTransaction,
    needsShiftOpen,
    activeShift,
    loading,
    shiftStatus: hasActiveShift ? 'OPEN' : 'CLOSED',
    refreshActiveShift
  }
}

/**
 * Hook for shift form management with validation
 */
export const useShiftForm = (initialData = {}) => {
  const [formData, setFormData] = useState({
    opening_cash: 0,
    closing_cash: 0,
    notes: '',
    ...initialData
  })
  const [validation, setValidation] = useState({ isValid: true, errors: [] })

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const resetForm = useCallback(() => {
    setFormData({
      opening_cash: 0,
      closing_cash: 0,
      notes: ''
    })
    setValidation({ isValid: true, errors: [] })
  }, [])

  const validateForm = useCallback((type = 'opening') => {
    let validationResult
    
    if (type === 'opening') {
      validationResult = validateShiftOpening(formData)
    } else if (type === 'closing') {
      validationResult = validateShiftClosing(formData)
    }
    
    setValidation(validationResult)
    return validationResult.isValid
  }, [formData])

  return {
    formData,
    validation,
    updateField,
    resetForm,
    validateForm,
    isValid: validation.isValid
  }
}

/**
 * Hook for cash entry form management
 */
export const useCashEntryForm = () => {
  const [formData, setFormData] = useState({
    type: '',
    amount: 0,
    description: ''
  })
  const [validation, setValidation] = useState({ isValid: true, errors: [] })

  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }, [])

  const resetForm = useCallback(() => {
    setFormData({
      type: '',
      amount: 0,
      description: ''
    })
    setValidation({ isValid: true, errors: [] })
  }, [])

  const validateForm = useCallback(() => {
    const validationResult = validateCashEntry(formData)
    setValidation(validationResult)
    return validationResult.isValid
  }, [formData])

  return {
    formData,
    validation,
    updateField,
    resetForm,
    validateForm,
    isValid: validation.isValid
  }
}